import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SchoolIcon from "@mui/icons-material/School";

import { StyledCard } from "../common/StyledCard";
import { SubjectResponseDto } from "../../shared/dtos/subject/SubjectResponseDto";

interface SubjectCardProps {
  subject: SubjectResponseDto;
  onEdit?: () => void;
  onDelete?: () => void;
}

const SubjectCard: React.FC<SubjectCardProps> = ({
  subject,
  onEdit,
  onDelete,
}) => {
  return (
    <StyledCard>
      <CardContent
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                flexWrap: "noWrap",
              }}
            >
              {subject.name}
            </Typography>
            <Chip
              label={subject.code}
              size="small"
              color="secondary"
              variant="outlined"
              icon={<MenuBookIcon fontSize="small" />}
              sx={{
                mt: 0.5,
                height: 24,
                fontSize: "0.75rem",
                fontWeight: "bold",
              }}
            />
          </Box>
          <Stack direction="row">
            {onEdit && (
              <Tooltip title="Edit Subject">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete Subject">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Body: Description */}
        <Box sx={{ flexGrow: 1, mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {subject.description || "No description provided."}
          </Typography>
        </Box>

        {/* Footer: Grade Levels */}
        <Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              mb: 1,
            }}
          >
            <SchoolIcon fontSize="small" color="action" />
            <Typography
              variant="caption"
              sx={{
                fontWeight: "bold",
                color: "text.secondary",
              }}
            >
              Applicable Grades:
            </Typography>
          </Stack>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {subject.applicableGradeLevelNames &&
            subject.applicableGradeLevelNames.length > 0 ? (
              subject.applicableGradeLevelNames.map((gradeName, index) => (
                <Chip
                  key={index}
                  label={gradeName}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  }}
                />
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                None assigned
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default SubjectCard;
